import * as React from "react";
import {
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { PaginatedResponse } from "@/shared/models/pagination.model";
import dayjs from "dayjs";
import { getContactMessages } from "@/services/getContactMessages";
import { contactMessagesTableColumns } from "./contact-messages-table-columns";
import { Skeleton } from "@/components/ui/skeleton";

function ContactMessagesTable() {
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(10); // Removed setLimit since it's not being used

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const contactMessageData = useQuery<PaginatedResponse>({
    queryKey: ["contactMessages", page],
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    queryFn: async () => {
      let response = await getContactMessages({ page, limit });

      response = {
        ...response,
        data: response?.data?.map((contactMessage: any) => ({
          id: contactMessage._id,
          createdAt: dayjs(String(contactMessage.createdAt)).format("MM/DD/YYYY h:mm A"),
          ...contactMessage
        })),
      };

      return response;
    },
  });

  // Update table configuration
  const table = useReactTable({
    data: Array.isArray(contactMessageData?.data?.data) ? contactMessageData.data.data : [],
    columns: contactMessagesTableColumns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    // Remove getPaginationRowModel since we're using server-side pagination
  });

  // Add pagination info component
  const PaginationInfo = () => {
    const { paging } = contactMessageData.data || {
      paging: { currentPage: 1, totalItems: 0, totalPages: 1 },
    };
    return (
      <div className="text-sm text-muted-foreground">
        Page {paging.currentPage} of {paging.totalPages} ({paging.totalItems}{" "}
        total contactMessages)
      </div>
    );
  };

  return (
    <div className="w-full md:p-2">
      {contactMessageData.isPending ? (
         [1, 2, 3, 4].map(() => (
              <div className="flex items-center space-x-4 w-full my-8">
                {/* <Skeleton className="h-12 w-12 rounded-full" /> */}
                <div className="space-y-2 w-full">
                  <Skeleton className="h-4 w-[100%]" />
                  <Skeleton className="h-4 w-[100%]" />
                </div>
              </div>
            ))
      ) : contactMessageData.isError ? (
        <div>{contactMessageData.error.message}</div>
      ) : (
        <>
          <div className="flex items-center gap-2 py-4">
            {/* <Input
              placeholder="Filter contactMessages..."
              value={
                (table.getColumn("service")?.getFilterValue() as string) ?? ""
              }
              onChange={(event) =>
                table.getColumn("service")?.setFilterValue(event.target.value)
              }
              className="max-w-sm"
            /> */}
            {/* <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="ml-auto">
                  Columns <ChevronDownIcon className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) =>
                          column.toggleVisibility(!!value)
                        }
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu> */}
          </div>
          <div className="rounded-md bcontactMessage">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={contactMessagesTableColumns.length}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-end space-x-2 py-4">
            <div className="flex-1">
              {contactMessageData.isSuccess && <PaginationInfo />}
            </div>
            <div className="space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page <= 1 || contactMessageData.isFetching}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={
                  !contactMessageData.data?.paging ||
                  page >= contactMessageData.data.paging.totalPages ||
                  contactMessageData.isFetching
                }
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ContactMessagesTable;
